import { useCallback } from 'react';

import { Box, Button, HStack, Separator } from '@chakra-ui/react';

import { IMAGE_SIZES, ImageRatio, ImageSize } from '@nocowanie/core';
import { RoomImage } from '@nocowanie/common-ui';

import { RoomDetails } from '@app/components/atoms/room-details';
import { RoomPictograms } from '@app/components/atoms/room-pictograms';
import { RoomServices } from '@app/components/atoms/room-services';
import { UnavailabilityCTA } from '@app/components/atoms/unavailability-cta';
import { ApartmentHeader } from '@app/components/molecules/apartment-header';
import { ApartmentVariants } from '@app/components/organisms/apartment-variants';
import { AppModalsEnum } from '@app/enums';
import { useWpResizer } from '@app/hooks';
import { accommodationSlice, commonUiStateSlice, useAppDispatch, useAppSelector } from '@app/state';

import { SingleApartmentProps } from './single-apartment.props';

export const SingleApartment = ({ apartment, ...props }: SingleApartmentProps): JSX.Element => {
    const dispatch = useAppDispatch();
    const wpresizer = useWpResizer();

    const handleModalOpen = useCallback(() => {
        setTimeout(
            () =>
                dispatch(
                    commonUiStateSlice.actions.setModalDataAction({
                        modalName: AppModalsEnum.ApartmentDetails,
                        modalData: apartment,
                    }),
                ),
            0,
        );
    }, [apartment]);

    const apartmentImage = useAppSelector(
        accommodationSlice.selectors.selectApartmentImage(apartment?.id),
    );

    const isNotAvailable = !apartment?.variants?.length;

    return (
        <Box data-testid="apartment-variants" position={'relative'} {...props}>
            <Separator />
            <Button
                variant={'plain'}
                colorPalette={'gray'}
                onClick={handleModalOpen}
                zIndex={'base'}
                whiteSpace={'normal'}
                textAlign={'left'}
                px={0}
                _hover={{
                    '& h3': {
                        transition: 'opacity',
                        opacity: 0.75,
                    },
                }}
            >
                <ApartmentHeader
                    name={apartment?.name ?? ''}
                    my={4}
                    lineClamp={1}
                    opacity={!apartment?.variants?.length ? 0.3 : undefined}
                />
            </Button>
            <HStack align={'flex-start'} justify={'space-between'} gap={4}>
                <Box
                    width={220}
                    opacity={!apartment?.variants?.length ? 0.3 : undefined}
                    zIndex={1}
                >
                    <RoomImage
                        flexShrink={0}
                        maxW={'100%'}
                        ratio={ImageRatio['16/9']}
                        imageProps={{
                            src:
                                apartmentImage &&
                                wpresizer(
                                    {
                                        ratio: ImageRatio['16/9'],
                                        size: ImageSize.Sm,
                                    },
                                    apartmentImage,
                                ),
                            htmlWidth: IMAGE_SIZES[ImageRatio['16/9']][ImageSize.Sm].width,
                            htmlHeight: IMAGE_SIZES[ImageRatio['16/9']][ImageSize.Sm].height,
                            alt: apartment?.name ?? '',
                        }}
                        cursor={'pointer'}
                        onClick={handleModalOpen}
                    />
                    <RoomPictograms
                        size={apartment?.size}
                        singleBeds={apartment?.singleBeds}
                        doubleBeds={apartment?.doubleBeds}
                        mt={5}
                        iconSize="sm"
                    />
                    <RoomServices
                        services={apartment?.services}
                        mt={5}
                        openModal={handleModalOpen}
                        headingSize={'sm'}
                        listingSize={'xs'}
                    />
                    <RoomDetails // for SEO only
                        apartment={apartment}
                        simplifyTags={true}
                        headingProps={{
                            fontSize: 'sm',
                        }}
                        fontSize={'xs'}
                        hidden
                    />
                </Box>
                {isNotAvailable ? (
                    <UnavailabilityCTA
                        buttonProps={{
                            minW: '220px',
                        }}
                        isSingleApartment={true}
                        labelsShort={true}
                        textProps={{
                            color: 'gray.900',
                            flex: 1,
                            fontSize: 'sm',
                        }}
                        flex={1}
                        gap={'gutterWidth'}
                        justifyContent={'space-between'}
                    />
                ) : (
                    <ApartmentVariants apartmentId={apartment.id} flex={1} />
                )}
            </HStack>
        </Box>
    );
};
